import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Bell, BellOff, BellRing, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// Convert base64 URL to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Check support and status on mount
  useEffect(() => {
    const checkSupport = async () => {
      // Check browser support
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);

      if (!supported) {
        setIsLoading(false);
        return;
      }

      // Check permission
      setPermission(Notification.permission);

      // Get VAPID public key
      try {
        const response = await api.get('/push/vapid-public-key');
        const data = await response.json();
        
        if (data.configured && data.public_key) {
          setVapidPublicKey(data.public_key);
        }
      } catch (error) {
        console.error('Failed to get VAPID key:', error);
      }

      // Check current subscription status
      try {
        const response = await api.get('/push/status');
        const data = await response.json();
        setIsSubscribed(data.subscribed);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
      }

      setIsLoading(false);
    };

    checkSupport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidPublicKey) {
      toast.error('Les notifications ne sont pas disponibles');
      return false;
    }

    setIsLoading(true);

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        toast.error('Permission de notification refusée');
        setIsLoading(false);
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const response = await api.post('/push/subscribe', {
        subscription: subscription.toJSON()
      });

      const data = await response.json();

      if (data.success) {
        setIsSubscribed(true);
        toast.success('Notifications activées !');
        return true;
      } else {
        throw new Error(data.message || 'Subscription failed');
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error('Échec de l\'activation des notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidPublicKey]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      // Unsubscribe from push manager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from backend
      await api.delete('/push/unsubscribe');

      setIsSubscribed(false);
      toast.success('Notifications désactivées');
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Échec de la désactivation');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendTestNotification = useCallback(async () => {
    try {
      const response = await api.post('/push/test', {
        title: '🔔 Test Notification',
        body: 'Les notifications fonctionnent correctement !',
        url: '/admin'
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Notification de test envoyée');
      } else {
        toast.error(data.message || 'Échec de l\'envoi');
      }
    } catch (error) {
      console.error('Failed to send test:', error);
      toast.error('Erreur lors de l\'envoi du test');
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
};

// Notification Bell Component for Header
export const NotificationBell = ({ className = '' }) => {
  const { 
    isSupported, 
    isSubscribed, 
    isLoading, 
    permission,
    subscribe, 
    unsubscribe,
    sendTestNotification 
  } = usePushNotifications();

  const [showMenu, setShowMenu] = useState(false);

  if (!isSupported) {
    return null;
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowMenu(!showMenu)}
        className={`relative ${isSubscribed ? 'text-tp-accent' : 'text-white/70'}`}
        disabled={isLoading}
        data-testid="notification-bell"
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="h-5 w-5" />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        {isSubscribed && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-tp-accent rounded-full border-2 border-tp-primary" />
        )}
      </Button>

      {/* Dropdown Menu */}
      {showMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
            <h4 className="font-medium text-gray-900 mb-3">Notifications Push</h4>
            
            {permission === 'denied' ? (
              <p className="text-sm text-red-600 mb-3">
                Les notifications sont bloquées dans votre navigateur. Veuillez les autoriser dans les paramètres.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-600">
                    {isSubscribed ? 'Activées' : 'Désactivées'}
                  </span>
                  <Button
                    variant={isSubscribed ? 'outline' : 'default'}
                    size="sm"
                    onClick={handleToggle}
                    disabled={isLoading}
                    className={isSubscribed ? '' : 'bg-tp-accent hover:bg-tp-accent-light'}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : isSubscribed ? (
                      <BellOff className="h-4 w-4 mr-1" />
                    ) : (
                      <Bell className="h-4 w-4 mr-1" />
                    )}
                    {isSubscribed ? 'Désactiver' : 'Activer'}
                  </Button>
                </div>

                {isSubscribed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-gray-600"
                    onClick={() => {
                      sendTestNotification();
                      setShowMenu(false);
                    }}
                  >
                    Envoyer une notification test
                  </Button>
                )}
              </>
            )}

            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Recevez des alertes pour les nouveaux leads et candidats.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
